// Pure hex-color helpers used to derive a full CSS palette from the small
// set of colors a user actually picks in the appearance editor.

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean
  const num = parseInt(full, 16)
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)))
  return '#' + [clamp(r), clamp(g), clamp(b)].map(v => v.toString(16).padStart(2, '0')).join('')
}

/** Mixes a hex color toward black (amount 0-1). */
export function darken(hex: string, amount: number): string {
  if (!/^#[0-9a-fA-F]{3,6}$/.test(hex)) return hex
  const [r, g, b] = hexToRgb(hex)
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount))
}

/** Mixes a hex color toward white (amount 0-1). */
export function lighten(hex: string, amount: number): string {
  if (!/^#[0-9a-fA-F]{3,6}$/.test(hex)) return hex
  const [r, g, b] = hexToRgb(hex)
  return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount)
}

/** Returns the color as an rgba() string with the given alpha (0-1). */
export function alpha(hex: string, a: number): string {
  if (!/^#[0-9a-fA-F]{3,6}$/.test(hex)) return hex
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`
}

const isHex = (v: string) => /^#[0-9a-fA-F]{3,6}$/.test(v)

/** Blends `from` toward `to` by `amount` (0-1). Works in both directions, so it
 *  behaves correctly on light themes as well as dark ones — unlike darken(). */
export function mix(from: string, to: string, amount: number): string {
  if (!isHex(from) || !isHex(to)) return from
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  return rgbToHex(
    a[0] + (b[0] - a[0]) * amount,
    a[1] + (b[1] - a[1]) * amount,
    a[2] + (b[2] - a[2]) * amount,
  )
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG 2.1 contrast ratio (1-21). Returns 1 for non-hex input so callers that
 *  compare against a threshold fail closed instead of silently passing. */
export function contrastRatio(a: string, b: string): number {
  if (!isHex(a) || !isHex(b)) return 1
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** WCAG AA for normal-size text. */
const AA_CONTRAST = 4.5

/**
 * Muted secondary text: body text blended toward the background, but never past
 * the point where it stops being readable.
 *
 * This used to be a flat `darken(textBody, 0.35)`. Two problems with that: it
 * only dims on dark themes (on a light one it *raises* contrast, so "muted"
 * looked bolder than body text), and being a fixed ratio it had no idea what it
 * was sitting on. On the default profile it landed at 2.55:1 against
 * --c-surface-2 — below AA, below even the 3:1 large-text floor — and it is the
 * colour of ~210 pieces of secondary text across the app.
 *
 * Now it walks the blend back until the result clears AA against the *darkest*
 * surface it can appear on, so every profile a user can build stays readable.
 * The trade is that on palettes whose body text is already close to the
 * background the muted step comes out subtle; separation there has to come from
 * size or weight, not from dimming further.
 */
export function mutedTextColor(textBody: string, bgBase: string, darkestSurface: string): string {
  if (!isHex(textBody) || !isHex(bgBase)) return textBody
  // A profile may define `surface` as rgba(); fall back to the base background,
  // which is what shows through a translucent surface anyway.
  const floorAgainst = isHex(darkestSurface) ? darkestSurface : bgBase

  for (let amount = 0.35; amount > 0; amount -= 0.01) {
    const candidate = mix(textBody, bgBase, amount)
    if (contrastRatio(candidate, floorAgainst) >= AA_CONTRAST) return candidate
  }
  // Body text itself is already below AA — nothing derived from it can pass, so
  // return it unchanged rather than inventing a colour outside the palette.
  return textBody
}

/**
 * Deck and tag colours are stored in two shapes: a hex literal from the colour
 * picker, or a `var(--c-…)` reference from the built-in presets. Contrast maths
 * needs a real value, so references have to be resolved first — otherwise
 * readableTextColor() is handed the string "var(--c-red)", fails its hex check
 * and returns it untouched, silently doing nothing.
 *
 * `lookup` is injected so this stays pure and testable; callers in the browser
 * pass a getComputedStyle-backed reader.
 */
export function resolveColorRef(value: string, lookup: (name: string) => string): string {
  const m = /^var\(\s*(--[\w-]+)\s*\)$/.exec(value.trim())
  if (!m) return value
  const resolved = lookup(m[1]!).trim()
  return resolved || value
}

/**
 * Nudges an accent colour until it is readable as *text* on `bg`, moving it away
 * from the background (lighter on dark themes, darker on light ones).
 *
 * Deck and tag accents are arbitrary user-picked colours, and the session card
 * renders them as words — the deck label, the answer heading. A red deck landed
 * at 3.25:1. No palette token can fix that, because the colour isn't ours.
 */
export function readableTextColor(accent: string, bg: string, target = AA_CONTRAST): string {
  if (!isHex(accent) || !isHex(bg)) return accent
  if (contrastRatio(accent, bg) >= target) return accent

  // Move away from the background: toward white on a dark bg, black on a light one.
  const towards = relativeLuminance(bg) < 0.5 ? '#FFFFFF' : '#000000'
  for (let amount = 0.05; amount <= 1; amount += 0.05) {
    const candidate = mix(accent, towards, amount)
    if (contrastRatio(candidate, bg) >= target) return candidate
  }
  return towards
}

/**
 * Text colour to place *on top of* `fill` — whichever of the app's own ink
 * colours reads better. Used where a user-picked accent becomes a button
 * background and something has to be legible on it.
 */
export function onColorText(fill: string, dark = '#0A0F0B', light = '#E8E0D0'): string {
  if (!isHex(fill)) return dark
  return contrastRatio(fill, dark) >= contrastRatio(fill, light) ? dark : light
}

/**
 * Adjusts a fill until `ink` is legible on top of it, preserving the hue and
 * moving only luminance.
 *
 * Picking the better of two inks isn't always enough: against a mid-tone accent
 * like #C24B3A neither near-black nor cream reaches AA (4.0:1 and 3.7:1). The
 * accent has to give a little. It moves away from the ink, so a dark-ink button
 * gets slightly lighter and a light-ink one slightly darker, which keeps the
 * deck's colour recognisable.
 */
export function fillForInk(fill: string, ink: string, target = AA_CONTRAST): string {
  if (!isHex(fill) || !isHex(ink)) return fill
  if (contrastRatio(fill, ink) >= target) return fill

  const towards = relativeLuminance(ink) < 0.5 ? '#FFFFFF' : '#000000'
  for (let amount = 0.02; amount <= 1; amount += 0.02) {
    const candidate = mix(fill, towards, amount)
    if (contrastRatio(candidate, ink) >= target) return candidate
  }
  return towards
}

/** Is this a light background? (Decides which way derived colours move.) */
export function isLightColor(hex: string): boolean {
  return isHex(hex) && relativeLuminance(hex) > 0.4
}

/**
 * The semantic hues the app encodes meaning with: ratings 1–5, deck colours,
 * states. Themes never pick these; they are adjusted per background so they
 * stay readable as text (AA) without losing their hue.
 */
export const SEMANTIC_COLORS = {
  red: '#CE6F61',
  amber: '#C8852A',
  blue: '#5A8ECC',
  purple: '#9A71F7',
} as const

/** How far note body copy sits from the title ink toward body text. On the
 *  default theme it reproduces the stock --c-cream-soft (index.css). */
const SOFT_STEP = 0.79

export interface DerivedColors {
  surface2: string
  border: string
  borderDim: string
  primaryDim: string
  primaryGlow: string
  primaryText: string
  /** Long-form body copy (note paragraphs): a step softer than titles. */
  creamSoft: string
  creamMuted: string
  /** Semantic text colours, readable on the theme's background. */
  redText: string
  amberText: string
  blueText: string
  purpleText: string
  isLight: boolean
}

/**
 * Derives the secondary palette (borders, dim/glow accent variants, muted text)
 * from the handful of colors the appearance editor exposes, so the user only
 * ever has to pick primary/background/surface/text colors and the rest of the
 * app stays visually coherent.
 */
export function deriveSecondaryColors(colors: {
  primary: string
  bgBase: string
  surface: string
  textBody: string
  textTitle?: string
}): DerivedColors {
  const bg = colors.bgBase
  // Light themes: surfaces and borders step toward the ink, not toward white —
  // lightening an off-white background produced borders nobody could see.
  const light = isLightColor(bg)
  const ink = colors.textTitle ?? colors.textBody
  const surface2 = light ? mix(colors.surface, ink, 0.05) : lighten(colors.surface, 0.06)
  const readable = (hex: string) => readableTextColor(hex, bg)
  return {
    surface2,
    border: light ? mix(bg, ink, 0.16) : lighten(bg, 0.12),
    borderDim: light ? mix(bg, ink, 0.08) : lighten(bg, 0.06),
    primaryDim: light ? mix(colors.primary, bg, 0.55) : darken(colors.primary, 0.3),
    primaryGlow: alpha(colors.primary, 0.12),
    // The accent is used as text (links, selected labels): always AA on the background.
    primaryText: readable(light ? colors.primary : lighten(colors.primary, 0.2)),
    // Note paragraphs. This used to be a fixed #B0A594 that no theme replaced,
    // so in light mode note text stayed light cream on paper (2.1:1). Now it is
    // the title ink stepped toward body text, which follows every theme and
    // the light and night variants.
    creamSoft: readable(mix(ink, colors.textBody, SOFT_STEP)),
    // surface2 is the darkest thing muted text ever sits on, so it's the one
    // that sets the floor — see mutedTextColor.
    creamMuted: mutedTextColor(colors.textBody, bg, surface2),
    redText: readable(SEMANTIC_COLORS.red),
    amberText: readable(SEMANTIC_COLORS.amber),
    blueText: readable(SEMANTIC_COLORS.blue),
    purpleText: readable(SEMANTIC_COLORS.purple),
    isLight: light,
  }
}
